import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import type { WaterRating, WaterRatingUpdate } from '@models/water-rating';
import type { WaterTestimonial, WaterTestimonialCreateOrUpdate } from '@models/water-testimonial';
import type { WaterCreateOrUpdateRequest, WaterDetail, WaterListParams, WaterListResponse } from '@models/waters';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CrmWatersService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api/crm/waters`;

  getWaters(params: WaterListParams): Observable<WaterListResponse> {
    let p = new HttpParams().set('page', params.page).set('pageSize', params.pageSize);

    const setIf = (key: string, value: string | number | boolean | null | undefined) => {
      if (value != null && value !== '') p = p.set(key, value);
    };

    setIf('search', params.search);
    setIf('siteId', params.siteId);
    setIf('countryId', params.countryId);
    setIf('exclusiveId', params.exclusiveId);
    setIf('isActive', params.isActive);
    setIf('dogs', params.dogs);
    setIf('rowingBoat', params.rowingBoat);
    setIf('baitBoat', params.baitBoat);
    setIf('restaurant', params.restaurant);
    setIf('hasAccommodations', params.hasAccommodations);
    setIf('ownTent', params.ownTent);
    setIf('ownCaravanCamper', params.ownCaravanCamper);
    setIf('carOnSpot', params.carOnSpot);
    setIf('pricePerSpot', params.pricePerSpot);
    setIf('week', params.week);
    setIf('midweek', params.midweek);
    setIf('weekend', params.weekend);
    setIf('difficultyMin', params.difficultyMin);
    setIf('difficultyMax', params.difficultyMax);
    setIf('waterSizeMin', params.waterSizeMin);
    setIf('waterSizeMax', params.waterSizeMax);
    setIf('spotsMin', params.spotsMin);
    setIf('spotsMax', params.spotsMax);
    setIf('coTravelerSpotsMin', params.coTravelerSpotsMin);
    setIf('coTravelerSpotsMax', params.coTravelerSpotsMax);
    setIf('carpWeightMin', params.carpWeightMin);
    setIf('carpWeightMax', params.carpWeightMax);
    setIf('priceMin', params.priceMin);
    setIf('priceMax', params.priceMax);
    setIf('averageRatingMin', params.averageRatingMin);
    setIf('averageRatingMax', params.averageRatingMax);
    setIf('amountOfRatingsMin', params.amountOfRatingsMin);
    setIf('amountOfRatingsMax', params.amountOfRatingsMax);
    setIf('viewsMin', params.viewsMin);
    setIf('viewsMax', params.viewsMax);
    setIf('lastUpdateFrom', params.lastUpdateFrom);
    setIf('lastUpdateTo', params.lastUpdateTo);

    return this.http.get<WaterListResponse>(this.baseUrl, { params: p });
  }

  getWater(id: number): Observable<WaterDetail> {
    return this.http.get<WaterDetail>(`${this.baseUrl}/${id}`);
  }

  createWater(dto: WaterCreateOrUpdateRequest): Observable<WaterDetail> {
    return this.http.post<WaterDetail>(this.baseUrl, dto);
  }

  updateWater(id: number, dto: WaterCreateOrUpdateRequest): Observable<WaterDetail> {
    return this.http.put<WaterDetail>(`${this.baseUrl}/${id}`, dto);
  }

  deleteWater(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  // Testimonials (water-specific)

  getTestimonials(waterId: number): Observable<WaterTestimonial[]> {
    return this.http.get<WaterTestimonial[]>(`${this.baseUrl}/${waterId}/testimonials`);
  }

  createTestimonial(waterId: number, dto: WaterTestimonialCreateOrUpdate): Observable<WaterTestimonial> {
    return this.http.post<WaterTestimonial>(`${this.baseUrl}/${waterId}/testimonials`, dto);
  }

  updateTestimonial(
    waterId: number,
    testimonialId: number,
    dto: WaterTestimonialCreateOrUpdate,
  ): Observable<WaterTestimonial> {
    return this.http.put<WaterTestimonial>(`${this.baseUrl}/${waterId}/testimonials/${testimonialId}`, dto);
  }

  deleteTestimonial(waterId: number, testimonialId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${waterId}/testimonials/${testimonialId}`);
  }

  // Ratings (public-submitted; view/edit/delete only)

  getRatings(waterId: number): Observable<WaterRating[]> {
    return this.http.get<WaterRating[]>(`${this.baseUrl}/${waterId}/ratings`);
  }

  updateRating(waterId: number, ratingId: number, dto: WaterRatingUpdate): Observable<WaterRating> {
    return this.http.put<WaterRating>(`${this.baseUrl}/${waterId}/ratings/${ratingId}`, dto);
  }

  deleteRating(waterId: number, ratingId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${waterId}/ratings/${ratingId}`);
  }
}
